import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { autoTune } from '../../services/api';
import EvaluatorParamsFields from '../EvaluatorParamsFields';
import OptimizationDatasetPicker from '../OptimizationDatasetPicker';
import TuningModalShell from '../TuningModalShell';
import EvaluationPreviewResultView from './EvaluationPreviewResultView';
import EvaluationRunResultView from './EvaluationRunResultView';
import type {
  DatasetSplit,
  EvaluationMode,
  EvaluatorParamsFormState,
} from '../../types/evaluation';
import type { EvaluationJob, EvaluationJobPreviewData, EvaluationRunAgainSeed } from '../../types/evaluationJob';
import {
  DATASET_SPLIT_LABELS,
  STANDALONE_EVALUATION_MODES,
  EVALUATION_MODE_LABELS,
  buildEvaluationRunRequestBody,
  createDefaultEvaluatorParamsForm,
  evaluationModeHasEvaluatorParams,
  mergeEvaluatorParamsFormForMode,
  normalizeEvaluationMode,
  validateEvaluatorParamsForm,
} from '../../types/evaluation';
import {
  extractEvaluationJobIdFromResponse,
  isTerminalEvaluationJobStatus,
  resolveEvaluationJobProgressDisplay,
  unwrapEvaluationPreviewPayload,
} from '../../utils/evaluationJobProgress';
import { getErrorMessage, formatEvaluationJobErrorMessage } from '../../utils/errorUtils';
import { getAvailableModelsForConfigurator } from '../../utils/configuratorModels';
import '../EvaluatorParamsFields.css';
import '../ConnectorConfigTuningFields.css';
import '../TuningModalShell.css';
import './EvaluationPreviewResultView.css';
import './EvaluationRunResultView.css';

export interface EvaluationRunModalProps {
  open: boolean;
  onClose: () => void;
  integrationId: string | null;
  datasets: string[];
  datasetsLoading?: boolean;
  buildConfig: () => Record<string, unknown> | null;
  onJobStarted?: (jobId: string) => void;
  /** Live job snapshot from Connector polling — used for running/result phases. */
  activeJob?: EvaluationJob | null;
  isJobRunning?: boolean;
  /** Prefill form when opening Run again from job history. */
  runAgainSeed?: EvaluationRunAgainSeed | null;
  runAgainKey?: string | null;
}

type EvaluationModalPhase = 'form' | 'running' | 'result' | 'failed';

const EvaluationRunModal: React.FC<EvaluationRunModalProps> = ({
  open,
  onClose,
  integrationId,
  datasets,
  datasetsLoading = false,
  buildConfig,
  onJobStarted,
  activeJob = null,
  isJobRunning = false,
  runAgainSeed = null,
  runAgainKey = null,
}) => {
  const [phase, setPhase] = useState<EvaluationModalPhase>('form');
  const [startedJobId, setStartedJobId] = useState<string | null>(null);
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);
  const [datasetSplit, setDatasetSplit] = useState<DatasetSplit>('validation');
  const [batchSize, setBatchSize] = useState('3');
  const [evaluationMode, setEvaluationMode] = useState<EvaluationMode>('openai-similarity');
  const [evaluatorParams, setEvaluatorParams] = useState<EvaluatorParamsFormState>(() =>
    createDefaultEvaluatorParamsForm('openai-similarity')
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<EvaluationJobPreviewData | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetFormErrors = useCallback(() => {
    setError(null);
    setPreviewError(null);
  }, []);

  const resetModalSession = useCallback(() => {
    setPhase('form');
    setStartedJobId(null);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setSubmitting(false);
    setError(null);
  }, []);

  useEffect(() => {
    if (open) return;
    resetModalSession();
  }, [open, resetModalSession]);

  useEffect(() => {
    if (!open || !runAgainSeed) return;
    resetModalSession();
    const mode = runAgainSeed.evaluationMode ?? 'openai-similarity';
    setEvaluationMode(mode);
    if (runAgainSeed.evaluatorParams) {
      setEvaluatorParams(mergeEvaluatorParamsFormForMode(mode, runAgainSeed.evaluatorParams));
    }
    if (runAgainSeed.datasetSplit) setDatasetSplit(runAgainSeed.datasetSplit);
    if (runAgainSeed.batchSize) setBatchSize(runAgainSeed.batchSize);
    if (runAgainSeed.datasetNames?.length) {
      setSelectedDatasets(runAgainSeed.datasetNames);
    }
  }, [open, runAgainKey, runAgainSeed, resetModalSession]);

  const datasetsKey = datasets.join('\u001f');
  useEffect(() => {
    if (!open || datasets.length === 0 || phase !== 'form') return;
    setSelectedDatasets((prev) => {
      const valid = prev.filter((d) => datasets.includes(d));
      if (valid.length > 0) return valid;
      return [datasets[0]];
    });
  }, [open, datasetsKey, datasets, phase]);

  const trackedJob =
    startedJobId && activeJob?.id === startedJobId ? activeJob : null;

  useEffect(() => {
    if (!open || !trackedJob) return;
    const status = String(trackedJob.status || '').toLowerCase();
    if (status === 'succeeded') {
      setPhase('result');
      setSubmitting(false);
      return;
    }
    if (status === 'failed' || status === 'cancelled') {
      setPhase('failed');
      setError(
        status === 'cancelled'
          ? 'Evaluation was cancelled.'
          : formatEvaluationJobErrorMessage(String(trackedJob.error || ''), 'Evaluation job failed.')
      );
      setSubmitting(false);
      return;
    }
    if (isJobRunning || !isTerminalEvaluationJobStatus(status)) {
      setPhase('running');
      setSubmitting(false);
    }
  }, [open, trackedJob, isJobRunning]);

  const buildRequestBody = useCallback(() => {
    const config = buildConfig();
    if (!config) return { error: 'Configure agents before running evaluation.' as const, body: null };
    if (selectedDatasets.length === 0) {
      return { error: 'Select at least one dataset.' as const, body: null };
    }
    const paramsError = validateEvaluatorParamsForm(evaluationMode, evaluatorParams);
    if (paramsError) return { error: paramsError, body: null };
    const batch_size = Number(batchSize);
    if (!Number.isFinite(batch_size) || batch_size <= 0) {
      return { error: 'Batch size must be a positive integer.', body: null };
    }
    const body = buildEvaluationRunRequestBody({
      config,
      dataset_names: selectedDatasets,
      dataset_split: datasetSplit,
      batch_size,
      evaluation_mode: evaluationMode,
      evaluatorParamsForm: evaluatorParams,
    });
    if (!body) return { error: 'Select at least one valid dataset.', body: null };
    return { error: null, body };
  }, [
    batchSize,
    buildConfig,
    datasetSplit,
    evaluationMode,
    evaluatorParams,
    selectedDatasets,
  ]);

  const canSubmit = useMemo(
    () => Boolean(integrationId && selectedDatasets.length > 0 && buildConfig()),
    [integrationId, selectedDatasets, buildConfig]
  );

  const availableModels = useMemo(
    () => getAvailableModelsForConfigurator(integrationId || ''),
    [integrationId]
  );

  const handleModeChange = (mode: EvaluationMode) => {
    setEvaluationMode(mode);
    setEvaluatorParams((prev) => mergeEvaluatorParamsFormForMode(mode, prev));
    setPreviewData(null);
    setPreviewError(null);
  };

  const handlePreview = async () => {
    if (!integrationId) return;
    const { error: validationError, body } = buildRequestBody();
    if (validationError || !body) {
      setPreviewError(validationError || 'Invalid evaluation request.');
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    try {
      const response = await autoTune.previewEvaluationJob(integrationId, body);
      if (response.success === false) {
        setPreviewError(response.error?.message || 'Preview failed.');
        return;
      }
      const parsed = unwrapEvaluationPreviewPayload(response.data ?? response);
      if (!parsed) {
        setPreviewError('Preview returned empty data.');
        return;
      }
      setPreviewData(parsed);
    } catch (e: unknown) {
      setPreviewError(getErrorMessage(e, 'Failed to load evaluation preview.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRun = async () => {
    if (!integrationId) return;
    const { error: validationError, body } = buildRequestBody();
    if (validationError || !body) {
      setError(validationError || 'Invalid evaluation request.');
      return;
    }
    setSubmitting(true);
    resetFormErrors();
    try {
      const response = await autoTune.createEvaluationJob(integrationId, body);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to create evaluation job.');
      }
      const jobId = extractEvaluationJobIdFromResponse(response) || response.data.id;
      if (!jobId) throw new Error('Server did not return an evaluation job id.');
      setStartedJobId(jobId);
      setPhase('running');
      onJobStarted?.(jobId);
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Failed to start evaluation.'));
      setPhase('failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRunAgain = () => {
    setPhase('form');
    setStartedJobId(null);
    setError(null);
    setPreviewData(null);
    setPreviewError(null);
  };

  if (!open) return null;

  const showForm = phase === 'form';
  const showRunning = phase === 'running';
  const showResult = phase === 'result' && trackedJob?.result != null;
  const showFailed = phase === 'failed';
  const formDisabled = submitting || previewLoading || showRunning;
  const progressMeta = trackedJob ? resolveEvaluationJobProgressDisplay(trackedJob) : null;

  const renderForm = () => (
    <div className="cct-form">
      {previewData ? (
        <section className="cct-card cct-card--compact" style={{ marginBottom: 16 }}>
          <h4 className="cct-card__title cct-card__title--with-icon">
            <span className="cct-card__title-icon" aria-hidden>
              <i className="fas fa-calculator" />
            </span>
            Cost preview
          </h4>
          <EvaluationPreviewResultView data={previewData} />
        </section>
      ) : null}

      <section className="cct-card">
        <h4 className="cct-card__title cct-card__title--with-icon">
          <span className="cct-card__title-icon" aria-hidden>
            <i className="fas fa-play" />
          </span>
          Run settings
        </h4>

        <div className="cct-control">
          <span className="cct-label" id="eval-run-dataset-label">
            Datasets
          </span>
          {datasetsLoading ? (
            <p className="evaluator-params__hint">Loading datasets…</p>
          ) : (
            <OptimizationDatasetPicker
              className="opt-dataset-picker--cct"
              datasets={datasets}
              selected={selectedDatasets}
              onChange={(next) => {
                setSelectedDatasets(next);
                setPreviewData(null);
              }}
              disabled={formDisabled}
              loading={datasetsLoading}
              emptyMessage="No datasets available for this integration."
            />
          )}
        </div>

        <div className="cct-card__grid cct-card__grid--2 cct-card__grid--stack-sm">
          <div className="cct-control">
            <label className="cct-label" htmlFor="eval-run-dataset-split">
              Dataset split
            </label>
            <select
              id="eval-run-dataset-split"
              className="cct-input cct-select"
              value={datasetSplit}
              onChange={(e) => {
                setDatasetSplit(e.target.value as DatasetSplit);
                setPreviewData(null);
              }}
              disabled={formDisabled}
            >
              {(Object.keys(DATASET_SPLIT_LABELS) as DatasetSplit[]).map((split) => (
                <option key={split} value={split}>
                  {DATASET_SPLIT_LABELS[split]}
                </option>
              ))}
            </select>
          </div>
          <div className="cct-control">
            <label className="cct-label" htmlFor="eval-run-evaluation-mode">
              Evaluation mode
            </label>
            <select
              id="eval-run-evaluation-mode"
              className="cct-input cct-select"
              value={evaluationMode}
              onChange={(e) => handleModeChange(normalizeEvaluationMode(e.target.value))}
              disabled={formDisabled}
            >
              {STANDALONE_EVALUATION_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {EVALUATION_MODE_LABELS[mode]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="cct-control cct-control--spaced">
          <label className="cct-label" htmlFor="eval-run-batch-size">
            Batch size
          </label>
          <input
            id="eval-run-batch-size"
            type="text"
            inputMode="numeric"
            className="cct-input"
            value={batchSize}
            onChange={(e) => {
              setBatchSize(e.target.value);
              setPreviewData(null);
            }}
            disabled={formDisabled}
          />
        </div>
      </section>

      {evaluationModeHasEvaluatorParams(evaluationMode) ? (
        <section className="cct-card cct-card--compact">
          <h4 className="cct-card__title cct-card__title--with-icon">
            <span className="cct-card__title-icon" aria-hidden>
              <i className="fas fa-gauge-high" />
            </span>
            Evaluator parameters
          </h4>
          <EvaluatorParamsFields
            idPrefix="eval-run-params"
            mode={evaluationMode}
            value={evaluatorParams}
            onChange={(next) => {
              setEvaluatorParams(next);
              setPreviewData(null);
            }}
            disabled={formDisabled}
            includeDirection={false}
            availableModels={availableModels}
          />
        </section>
      ) : null}
    </div>
  );

  return (
    <TuningModalShell
      onClose={onClose}
      title={
        showResult
          ? 'Evaluation result'
          : showRunning
            ? 'Evaluation in progress'
            : showFailed
              ? 'Evaluation failed'
              : 'Run evaluation'
      }
      subtitle={
        showResult
          ? 'Review scores and usage, then adjust settings and run again if needed.'
          : showRunning
            ? 'Progress updates on the page below. You can close this dialog and keep tracking the job.'
            : showFailed
              ? 'Update settings below and run again, or close this dialog.'
              : 'Evaluate the current agent configuration against a dataset batch.'
      }
      iconClassName={showResult ? 'fas fa-chart-pie' : 'fas fa-chart-line'}
      size={showResult ? 'result' : 'eval'}
      variant="eval"
      footer={
        <>
          <button
            type="button"
            className="tuning-modal__btn tuning-modal__btn--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Close
          </button>
          {showResult ? (
            <button
              type="button"
              className="tuning-modal__btn tuning-modal__btn--primary"
              onClick={handleRunAgain}
            >
              Run again
            </button>
          ) : showForm || showFailed ? (
            <>
              <button
                type="button"
                className="tuning-modal__btn tuning-modal__btn--preview"
                onClick={() => void handlePreview()}
                disabled={formDisabled || !canSubmit}
              >
                {previewLoading ? (
                  <>
                    <div className="spinner spinner--small" style={{ width: 16, height: 16 }} />
                    <span>Loading…</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-calculator" aria-hidden />
                    <span>Preview cost</span>
                  </>
                )}
              </button>
              <button
                type="button"
                className="tuning-modal__btn tuning-modal__btn--primary"
                onClick={() => void handleRun()}
                disabled={formDisabled || !canSubmit}
              >
                {submitting ? 'Starting…' : 'Run evaluation'}
              </button>
            </>
          ) : null}
        </>
      }
    >
      {error && (showFailed || showForm) ? (
        <div className="tuning-modal__alert tuning-modal__alert--error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden />
          <span>{error}</span>
        </div>
      ) : null}

      {previewError && showForm ? (
        <div className="tuning-modal__alert tuning-modal__alert--error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden />
          <span>{previewError}</span>
        </div>
      ) : null}

      {showRunning ? (
        <p className="evaluator-params__hint" style={{ marginBottom: 16 }}>
          {progressMeta?.message || 'Evaluation is running.'}
          {progressMeta?.meta ? ` ${progressMeta.meta}` : ''} Track progress in the job panel below.
        </p>
      ) : null}

      {showResult ? <EvaluationRunResultView data={trackedJob!.result} /> : null}

      {showForm || showFailed ? renderForm() : null}
    </TuningModalShell>
  );
};

export default EvaluationRunModal;
